import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, ClipboardList, Clock } from 'lucide-react';
import { pollsAPI } from '../../services/api';

interface PollNotification {
  id: number;
  title: string;
  description: string | null;
  deadline: string | null;
  created_at: string;
  creator_first_name: string;
  creator_last_name: string;
  user_has_responded: number;
}

interface PollNotificationsDropdownProps {
  count: number;
  onNotificationClick?: () => void;
  onPollClick?: (pollId: number) => void;
}

const PollNotificationsDropdown: React.FC<PollNotificationsDropdownProps> = ({ 
  count, 
  onNotificationClick,
  onPollClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<PollNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const data = await pollsAPI.getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePollClick = (pollId: number) => {
    setIsOpen(false);
    if (onPollClick) {
      onPollClick(pollId);
    } else if (onNotificationClick) {
      // Fallback to general navigation
      onNotificationClick();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="relative p-2 rounded-lg transition-colors hover:bg-gray-100"
        title={count > 0 ? `${count} pending poll(s)` : 'No pending polls'}
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {count > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-[10000] max-h-96 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Poll Notifications</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)] mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No pending polls</p>
              <p className="text-xs text-gray-500 mt-1">You have responded to all available polls</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handlePollClick(notification.id)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <ClipboardList className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                        {notification.title}
                      </h4>
                      {notification.description && (
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                          {notification.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>
                          By {notification.creator_first_name} {notification.creator_last_name}
                        </span>
                        {notification.deadline && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {(() => {
                              // Parse deadline - handle both MySQL datetime and ISO string formats
                              let dateStr = String(notification.deadline).trim();
                              if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return '';
                              
                              // If it's an ISO string with 'T' or 'Z', extract the date/time parts
                              // Format: "2026-01-27T17:00:00.000Z" or "2026-01-27T17:00:00"
                              if (dateStr.includes('T')) {
                                // Remove 'Z' if present and extract date/time
                                dateStr = dateStr.replace('Z', '').replace(/\.\d+$/, ''); // Remove milliseconds and Z
                                const [datePart, timePart] = dateStr.split('T');
                                if (datePart && timePart) {
                                  const [year, month, day] = datePart.split('-').map(Number);
                                  const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
                                  const date = new Date(year, month - 1, day, hours, minutes, seconds);
                                  if (!isNaN(date.getTime())) {
                                    return date.toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  }
                                }
                              }
                              
                              // Check if it matches MySQL datetime format: "YYYY-MM-DD HH:MM:SS"
                              const mysqlDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/;
                              const match = dateStr.match(mysqlDateTimePattern);
                              
                              if (match) {
                                try {
                                  // Parse manually to ensure local time (not UTC)
                                  const year = parseInt(match[1], 10);
                                  const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
                                  const day = parseInt(match[3], 10);
                                  const hours = parseInt(match[4], 10);
                                  const minutes = parseInt(match[5], 10);
                                  const seconds = parseInt(match[6], 10);
                                  
                                  const date = new Date(year, month, day, hours, minutes, seconds);
                                  if (!isNaN(date.getTime())) {
                                    return date.toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  }
                                } catch (e) {
                                  // Ignore
                                }
                              }
                              
                              // Last resort: try standard Date parsing
                              try {
                                const fallbackDate = new Date(dateStr);
                                if (!isNaN(fallbackDate.getTime())) {
                                  return fallbackDate.toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                }
                              } catch (e) {
                                // Ignore
                              }
                              
                              return dateStr; // Last resort: return original string
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  if (onNotificationClick) {
                    onNotificationClick();
                  }
                }}
                className="w-full text-sm text-[var(--accent)] hover:underline font-medium"
              >
                View All Polls
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PollNotificationsDropdown;
